/** 语音 Gateway 使用的 PCM16 little-endian 编解码和流式重采样。 */

export class StreamingResampler {
  private inputRate = 0;
  private outputRate = 0;
  private pending = new Float32Array(0);
  private position = 0;
  private totalInput = 0;
  private outputCount = 0;

  reset(): void {
    this.inputRate = 0;
    this.outputRate = 0;
    this.pending = new Float32Array(0);
    this.position = 0;
    this.totalInput = 0;
    this.outputCount = 0;
  }

  process(
    input: Float32Array,
    inputRate: number,
    outputRate: number,
  ): Float32Array {
    if (input.length === 0) return new Float32Array(0);
    if (inputRate !== this.inputRate || outputRate !== this.outputRate) {
      this.reset();
      this.inputRate = inputRate;
      this.outputRate = outputRate;
    }
    if (inputRate === outputRate) return input;
    const joined = new Float32Array(this.pending.length + input.length);
    joined.set(this.pending);
    joined.set(input, this.pending.length);
    this.pending = joined;
    this.totalInput += input.length;

    const ratio = this.inputRate / this.outputRate;
    const targetCount = Math.max(1, Math.round(this.totalInput / ratio));
    const output: number[] = [];
    while (
      this.position + 1 < this.pending.length &&
      this.outputCount < targetCount
    ) {
      const before = Math.floor(this.position);
      const fraction = this.position - before;
      const after = Math.min(this.pending.length - 1, before + 1);
      const beforeValue = this.pending[before] ?? 0;
      const afterValue = this.pending[after] ?? beforeValue;
      output.push(beforeValue * (1 - fraction) + afterValue * fraction);
      this.position += ratio;
      this.outputCount += 1;
    }
    const consumed = Math.min(this.pending.length, Math.floor(this.position));
    if (consumed > 0) {
      this.pending = this.pending.slice(consumed);
      this.position -= consumed;
    }
    return Float32Array.from(output);
  }
}

export function encodePcm16(samples: Float32Array): string {
  const bytes = new Uint8Array(samples.length * 2);
  const view = new DataView(bytes.buffer);
  for (let index = 0; index < samples.length; index += 1) {
    const value = Math.max(-1, Math.min(1, samples[index] ?? 0));
    view.setInt16(index * 2, value * 0x7fff, true);
  }
  let binary = "";
  for (let index = 0; index < bytes.length; index += 0x8000)
    binary += String.fromCharCode(...bytes.subarray(index, index + 0x8000));
  return btoa(binary);
}

export function decodePcm16(base64: string): Float32Array {
  const binary = atob(base64);
  const bytes = Uint8Array.from(binary, (character) => character.charCodeAt(0));
  if (bytes.length % 2 !== 0)
    throw new Error("PCM16 audio has an incomplete sample");
  const view = new DataView(bytes.buffer);
  const samples = new Float32Array(bytes.length / 2);
  for (let index = 0; index < samples.length; index += 1)
    samples[index] = view.getInt16(index * 2, true) / 0x8000;
  return samples;
}
